import { useState } from "react";
import { Drawer, Spin, Alert, message } from "antd";
import { DollarOutlined } from "@ant-design/icons";
import {
  drawerTitle,
  drawerFooter,
} from "../../../../components";
import {
  useTenantBillingQuery,
  useTenantPaymentsQuery,
  useRecordTenantPaymentMutation,
  useTerminateTenantMutation,
  useReactivateTenantMutation,
} from "../../../../Services";
import { TenantBillingSummary } from "./TenantBillingSummary";
import { TenantPaymentActions } from "./TenantPaymentActions";
import { TenantPaymentHistory } from "./TenantPaymentHistory";

interface Props {
  open: boolean;
  tenantId: string | undefined;
  onClose: () => void;
  onAssignPackage?: () => void;
}

export const TenantPaymentDrawer = ({
  open,
  tenantId,
  onClose,
  onAssignPackage,
}: Props) => {
  const { data: billing, isLoading, refetch } = useTenantBillingQuery(tenantId);
  const { data: payments, isLoading: paymentsLoading } =
    useTenantPaymentsQuery(tenantId);
  const recordMutation = useRecordTenantPaymentMutation();
  const terminateMutation = useTerminateTenantMutation();
  const reactivateMutation = useReactivateTenantMutation();

  const [formOpen, setFormOpen] = useState<null | "record" | "reactivate">(
    null,
  );
  const [amount, setAmount] = useState<number | null>(null);
  const [method, setMethod] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");

  const b = billing;
  const isTerminated = !!b?.is_terminated;

  const openForm = (mode: "record" | "reactivate") => {
    setAmount(b?.monthly_amount ? Number(b.monthly_amount) : null);
    setMethod("");
    setReference("");
    setNote("");
    setFormOpen(mode);
  };

  const closeForm = () => {
    setFormOpen(null);
    setAmount(null);
    setMethod("");
    setReference("");
    setNote("");
  };

  const handleRecord = async () => {
    if (!tenantId) return;
    if (amount === null || amount <= 0) {
      message.warning("Enter a valid payment amount.");
      return;
    }
    try {
      const res = await recordMutation.mutateAsync({
        tenant_id: tenantId,
        amount,
        payment_method: method.trim(),
        reference: reference.trim(),
        note: note.trim(),
      });
      if (res?.success) {
        message.success(res.message || "Payment recorded.");
        closeForm();
        refetch();
      } else {
        throw new Error(res?.message || "Failed to record payment.");
      }
    } catch (err: any) {
      message.error(err.message || "Failed to record payment.");
    }
  };

  const handleTerminate = async () => {
    if (!tenantId) return;
    try {
      const res = await terminateMutation.mutateAsync({ tenant_id: tenantId });
      if (res?.success) {
        message.success(res.message || "Tenant terminated.");
        refetch();
      } else {
        throw new Error(res?.message || "Failed to terminate tenant.");
      }
    } catch (err: any) {
      message.error(err.message || "Failed to terminate tenant.");
    }
  };

  const handleReactivate = async () => {
    if (!tenantId) return;
    if (amount === null || amount <= 0) {
      message.warning("Enter the payment amount to reactivate.");
      return;
    }
    try {
      const res = await reactivateMutation.mutateAsync({
        tenant_id: tenantId,
        payment: {
          amount,
          payment_method: method.trim(),
          reference: reference.trim(),
          note: note.trim(),
        },
      });
      if (res?.success) {
        message.success(res.message || "Tenant reactivated.");
        closeForm();
        refetch();
      } else {
        throw new Error(res?.message || "Failed to reactivate tenant.");
      }
    } catch (err: any) {
      message.error(err.message || "Failed to reactivate tenant.");
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={800}
      title={drawerTitle("Billing & Subscription", <DollarOutlined />)}
      footer={drawerFooter({
        actions: [{ label: "Close", onClick: onClose }],
      })}
    >
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spin />
        </div>
      ) : !b ? (
        <Alert type="error" message="Unable to load billing details." />
      ) : (
        <div className="flex flex-col gap-4">
          <TenantBillingSummary billing={b} onAssignPackage={onAssignPackage} />

          <TenantPaymentActions
            isTerminated={isTerminated}
            onOpenForm={openForm}
            formOpen={formOpen}
            onCloseForm={closeForm}
            amount={amount}
            onAmountChange={setAmount}
            method={method}
            onMethodChange={setMethod}
            reference={reference}
            onReferenceChange={setReference}
            note={note}
            onNoteChange={setNote}
            onRecord={handleRecord}
            onTerminate={handleTerminate}
            onReactivate={handleReactivate}
            recordPending={recordMutation.isPending}
            terminatePending={terminateMutation.isPending}
            reactivatePending={reactivateMutation.isPending}
          />

          <TenantPaymentHistory
            payments={payments}
            paymentsLoading={paymentsLoading}
          />
        </div>
      )}
    </Drawer>
  );
};

export default TenantPaymentDrawer;
